/**
 * CR500 Phase 2B: FinePrintBlock
 * 
 * Renders the fine print / exclusions section in deal details.
 * Supports maxLines config for flip mode truncation.
 */

import React from "react";
import type { DealCardBlockProps } from "@/lib/deal-cards/specV2/types";
import type { Deal } from "@/lib/types/deals";
import { cn } from "@/lib/utils";

export const FinePrintBlock: React.FC<DealCardBlockProps<Deal>> = ({
  deal,
  block,
  context,
}) => {
  // Don't render if block is disabled
  if (!block.enabled) {
    return null;
  }

  // Get fine print content from deal (exclusions, disclaimers, etc.)
  const finePrint = deal.exclusions || deal.disclaimer_text || null;
  
  // If no fine print content, don't render
  if (!finePrint) {
    return null;
  }

  // Get maxLines from block config (used in flip mode)
  const maxLines = block.config?.maxLines as number | undefined;
  
  // Line clamp class based on maxLines
  const clampClass = maxLines ? `line-clamp-${maxLines}` : undefined;

  return (
    <div 
      className="space-y-1"
      data-block="fine_print"
      data-block-id={block.id}
    >
      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        {context.labels?.fine_print_title || "Fine Print"}
      </h4>
      <p className={cn(
        "text-sm text-muted-foreground",
        clampClass,
      )}>
        {finePrint}
      </p>
    </div>
  );
};

export default FinePrintBlock;
